/**
 * Save slots.
 *
 * Persistence lives in the shell, never in the engine — the engine only knows how
 * to turn a state into a string and back. That separation is what lets the same
 * engine run in a browser, in Node for the test harness, and eventually in a Web
 * Worker, none of which agree on how storage works.
 *
 * Phase 0 used localStorage, when a save was a few tens of kilobytes. A late game on a
 * huge map is about a megabyte, and localStorage holds about five in all, so saves now
 * live in IndexedDB (Phase 15, ADR-0078) — which holds hundreds. A save still found in
 * localStorage from before is read from there and moved across. localStorage remains the
 * fallback where IndexedDB is unavailable.
 */

const PREFIX = 'epoch.save.';
export const SLOT_COUNT = 5;

export interface SlotInfo {
  slot: number;
  seed: string;
  turn: number;
  savedAt: number;
  bytes: number;
}

function storage(): Storage | null {
  try {
    // Private windows and blocked site-data both throw on access, not on use.
    const probe = '__epoch_probe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return window.localStorage;
  } catch {
    return null;
  }
}

// ─── IndexedDB: one object store of strings, keyed like the old localStorage keys ───

const DB_NAME = 'epoch';
const STORE = 'saves';
let opening: Promise<IDBDatabase | null> | null = null;

function database(): Promise<IDBDatabase | null> {
  opening ??= new Promise((resolve) => {
    try {
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => request.result.createObjectStore(STORE);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return opening;
}

function inStore<T>(db: IDBDatabase, mode: IDBTransactionMode, act: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const request = act(db.transaction(STORE, mode).objectStore(STORE));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Storage failed'));
  });
}

async function put(key: string, json: string, full: string): Promise<void> {
  const db = await database();
  if (db) {
    try {
      await inStore(db, 'readwrite', (s) => s.put(json, key));
      storage()?.removeItem(key);
      return;
    } catch {
      throw new Error(full);
    }
  }
  const store = storage();
  if (!store) throw new Error('This browser is blocking site storage, so games cannot be saved here.');
  try {
    store.setItem(key, json);
  } catch {
    throw new Error(full);
  }
}

async function get(key: string): Promise<string | null> {
  const db = await database();
  if (db) {
    const found = await inStore<string | undefined>(db, 'readonly', (s) => s.get(key) as IDBRequest<string | undefined>).catch(() => undefined);
    if (found !== undefined) return found;
  }
  // A save from before the move: read it, and move it across while we are here.
  const legacy = storage()?.getItem(key) ?? null;
  if (legacy !== null && db) {
    try {
      await inStore(db, 'readwrite', (s) => s.put(legacy, key));
      storage()?.removeItem(key);
    } catch {
      // Left where it was; it still loads from there.
    }
  }
  return legacy;
}

async function remove(key: string): Promise<void> {
  const db = await database();
  if (db) await inStore(db, 'readwrite', (s) => s.delete(key)).catch(() => undefined);
  storage()?.removeItem(key);
}

export async function writeSlot(slot: number, json: string): Promise<void> {
  await put(PREFIX + slot, json, 'There is not enough browser storage left to write that save.');
}

export async function readSlot(slot: number): Promise<string | null> {
  return get(PREFIX + slot);
}

export async function clearSlot(slot: number): Promise<void> {
  await remove(PREFIX + slot);
}

/** Summarise every occupied slot, skipping any that have become unreadable. */
export async function listSlots(): Promise<SlotInfo[]> {
  const out: SlotInfo[] = [];
  for (let slot = 1; slot <= SLOT_COUNT; slot++) {
    const json = await readSlot(slot);
    if (!json) continue;
    try {
      const parsed = JSON.parse(json) as { meta?: { seed?: string; turn?: number; createdAt?: number } };
      out.push({
        slot,
        seed: parsed.meta?.seed ?? '(unknown)',
        turn: parsed.meta?.turn ?? 0,
        savedAt: parsed.meta?.createdAt ?? 0,
        bytes: json.length,
      });
    } catch {
      // A corrupt slot is reported as occupied-but-unreadable rather than crashing the list.
      out.push({ slot, seed: '(unreadable)', turn: 0, savedAt: 0, bytes: json.length });
    }
  }
  return out;
}

// ─── autosaves ─────────────────────────────────────────────────────────────────

/**
 * Autosaves are kept apart from the numbered slots a player chooses.
 *
 * They rotate: with three of them the game always holds the last three turns, so a
 * mistake noticed a turn late is still recoverable, and an autosave can never quietly
 * overwrite a save someone made on purpose.
 */
const AUTO_PREFIX = 'epoch.autosave.';

export interface AutosaveInfo {
  index: number;
  turn: number;
  bytes: number;
}

export async function writeAutosave(turn: number, slots: number, json: string): Promise<number> {
  const index = ((turn - 1) % slots) + 1;
  await put(AUTO_PREFIX + index, json, 'There is not enough browser storage left to autosave.');
  return index;
}

export async function readAutosave(index: number): Promise<string | null> {
  return get(AUTO_PREFIX + index);
}

/** Every autosave that exists, most recent turn first. */
export async function listAutosaves(slots: number): Promise<AutosaveInfo[]> {
  const out: AutosaveInfo[] = [];
  for (let index = 1; index <= slots; index++) {
    const json = await readAutosave(index);
    if (!json) continue;
    try {
      const parsed = JSON.parse(json) as { meta?: { turn?: number } };
      out.push({ index, turn: parsed.meta?.turn ?? 0, bytes: json.length });
    } catch {
      // A corrupt autosave is simply not offered.
    }
  }
  return out.sort((a, b) => b.turn - a.turn);
}
